import React from "react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import OTAList from "@/components/OTAList";
import FinalCTA from "@/components/FinalCTA";
import { CheckCircle2, Repeat2, ShieldCheck, AlertTriangle, Layers } from "lucide-react";
import { useT } from "@/hooks/useT";

export default function ChannelManager() {
  const t = useT();
  const CARDS = [
    { icon: Repeat2,        title: t("Rezervimet rrjedhin në të dy drejtimet","Bookings flow both ways"),
      text: t("Çdo ndryshim që bëni në kalendar shfaqet menjëherë në kanalet ku jeni listuar. Çdo rezervim që vjen nga një kanal hyn vetë në kalendarin tuaj.",
              "Every change you make in the calendar shows up on every channel instantly. Every booking that comes from a channel lands straight in your calendar.") },
    { icon: ShieldCheck,    title: t("Pa rezervime të dyfishta","No double bookings"),
      text: t("Kur shitet një dhomë, ajo largohet menjëherë nga kanalet e tjera. Pa stres për konflikte rezervimi.",
              "When a room sells, it disappears from every other channel instantly. No stress about booking conflicts.") },
    { icon: Layers,         title: t("Të gjitha çmimet në një vend","Every rate in one place"),
      text: t("Çmimet, restriksionet (min-stay, close-to-arrival) dhe disponueshmëria — gjithçka nga një ekran.",
              "Rates, restrictions (min-stay, close-to-arrival) and availability — all from a single screen.") },
    { icon: AlertTriangle,  title: t("Njoftime kur diçka shkon keq","Alerts when something goes wrong"),
      text: t("Nëse një kanal del jashtë linje ose ka konflikt, sistemi ju njofton menjëherë.",
              "If a channel goes offline or a conflict appears, the system notifies you immediately.") },
  ];
  const BULLETS = [
    t("Sinkronizim i rezervimeve me Booking.com","Two-way sync with Booking.com"),
    t("Menaxhim i disponueshmërisë së dhomave","Full room availability management"),
    t("Pa rezervime të dyfishta","No double bookings"),
    t("Çmime dhe restriksione në një vend","Rates and restrictions in one place"),
    t("Sistem rezervimesh për disa objekte","Multi-property reservations"),
    t("Funksionon për hotele dhe apartamente","Works for hotels and apartments"),
  ];
  return (
    <>
      <SEO
        title={t("Channel Manager për Hotele — Booking.com, Airbnb, Expedia", "Hotel Channel Manager — Booking.com, Airbnb, Expedia")}
        description={t(
          "Channel Manager nga Akomoda: Booking.com, Airbnb, Expedia, Agoda, Vrbo dhe Google Hotels — të lidhura me kalendarin tuaj. Pa rezervime të dyfishta.",
          "Akomoda's Channel Manager: Booking.com, Airbnb, Expedia, Agoda, Vrbo and Google Hotels — connected to your calendar. No double bookings."
        )}
      />
      <PageHeader
        eyebrow="Channel Manager"
        title={t("Booking.com, Airbnb, Expedia — në një kalendar","Booking.com, Airbnb, Expedia — in one calendar")}
        description={t(
          "Çmimet, disponueshmëria dhe rezervimet menaxhohen nga një vend. Pa rezervime të dyfishta. Pa kalim manual nga ekrani në ekran.",
          "Rates, availability and reservations managed from one place. No double bookings. No manual jumping from screen to screen."
        )}
        ctas={[
          { to: "/demo",            label: t("Kërko Demo","Request Demo") },
          { to: "/booking-engine",  label: "Booking Engine", variant: "ghost" },
        ]}
        breadcrumb={[
          { label: t("Platforma","Platform"), to: "/platforma" },
          { label: "Channel Manager" },
        ]}
      />
      <section className="bg-surface-1">
        <div className="container-akm py-14 md:py-20">
          <div className="rounded-xl border border-line bg-white p-6 shadow-card">
            <div className="mb-4 text-xs font-semibold uppercase tracking-wider text-slate2">{t("Kanalet e mbështetura","Supported channels")}</div>
            <OTAList />
            <div className="mt-4 text-xs text-slate2">{t("Dhe shumë të tjera përmes integrimit API.","And many more through API integration.")}</div>
          </div>
        </div>
      </section>
      <section className="bg-surface-2">
        <div className="container-akm grid gap-10 py-14 md:grid-cols-2 md:py-20">
          {CARDS.map(({ icon: Icon, title, text }) => (
            <div key={title} className="card-akm">
              <Icon size={22} className="text-[#FF7A00]" />
              <h3 className="h3-akm mt-2">{title}</h3>
              <p className="mt-2 text-sm text-slate2">{text}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-surface-1">
        <div className="container-akm py-14 md:py-20">
          <div className="max-w-2xl">
            <div className="eyebrow">{t("Si funksionon","How it works")}</div>
            <h2 className="h2-akm mt-3">{t("Si lidhet Akomoda me kanalet e rezervimit","How Akomoda connects to booking channels")}</h2>
          </div>
          <div className="mt-5 grid gap-4 text-[15px] leading-relaxed text-slate2 md:grid-cols-2">
            <p>
              {t(
                "Akomoda lidhet drejtpërdrejt me Booking.com, Expedia, Airbnb dhe Agoda përmes API-ve të tyre zyrtare. Sapo një klient bën një rezervim në njërin nga këto kanale, sistemi e merr menjëherë, e fut në kalendar dhe e fsheh dhomën nga kanalet e tjera.",
                "Akomoda connects directly to Booking.com, Expedia, Airbnb and Agoda through their official APIs. The moment a guest books on any of those channels, the system picks it up, adds it to the calendar and hides the room from every other channel."
              )}
            </p>
            <p>
              {t(
                "Përveç kanaleve kryesore, Akomoda integron edhe kanale të tjera përmes API-ve standarde — kështu mund të lidhni Vrbo, platforma lokale të rezervimit dhe shumë të tjera, pa siklet teknik.",
                "Beyond the major channels, Akomoda also integrates other channels through standard APIs — so you can plug in Vrbo, local booking platforms and many more without any technical hassle."
              )}
            </p>
          </div>
          <ul className="mt-6 grid gap-2 text-sm sm:grid-cols-2">
            {BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-ink">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#FF7A00]" /> {b}
              </li>
            ))}
          </ul>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
